/**
 * MqttAudioManager.ts
 * MQTT音频管理器
 * 
 * 功能：
 * - 管理音频数据的MQTT传输
 * - 处理音频上行和下行
 * - 管理频谱数据订阅
 * - 支持多设备音频分发
 */

import { MqttManager, MqttEventCallbacks } from '../connections/MqttManager';
import { AudioFrameConstants } from '../../constants/AudioFrameConstants';
import {
  getMqttAudioDownstreamCodec2Topic,
  getMqttAudioDownstreamKindFromTopic,
  getMqttAudioDownstreamPcmTopic,
  getMqttAudioUpstreamCodec2UserTopic,
  getMqttAudioUpstreamUserTopic,
  type MqttAudioDownstreamKind,
} from '../../constants/Codec2Constants';

/** PCM → `audio/user/{id}/upstream`；Codec2 → `audio/user/{id}/upstream/c2` */
export type MqttVoiceUplinkRoute = 'user_pcm' | 'user_codec2';

/**
 * 与后端 `wavelink.udp.audio-mqtt.batch.enabled` 对齐：为 true/1/yes 时表示下行 payload 为
 * **多帧原始 PCM 首尾相接**（与《MQTT 设备下行音频批量格式说明》一致），**无** 2 字节长度前缀；
 * 前端将整包一次性交给播放链，等价于连续收到多条「单帧 MQTT」后按顺序拼接的 PCM 流。
 * 关闭时：整段 payload 即单帧（与历史未开批量时一致）。
 */
function isMqttAudioDownstreamBatchEnabled(): boolean {
  const v = process.env.REACT_APP_MQTT_AUDIO_DOWNSTREAM_BATCH?.trim().toLowerCase();
  if (!v) {
    return false;
  }
  return v === '1' || v === 'true' || v === 'yes';
}

/** 开发环境或 .env 设 REACT_APP_DEBUG_AUDIO_DOWNSTREAM=1 时输出汇总日志（生产 build 若启用 terser drop_console 仍会被剔除） */
function shouldLogAudioDownstreamVerbose(): boolean {
  if (process.env.NODE_ENV === 'development') {
    return true;
  }
  const v = process.env.REACT_APP_DEBUG_AUDIO_DOWNSTREAM?.trim().toLowerCase();
  return v === '1' || v === 'true' || v === 'yes';
}

/** 仅当 REACT_APP_DEBUG_MQTT_DOWNSTREAM_PAYLOAD=1 时打印 [WaveLink MQTT downstream payload] 全量字节（默认关闭） */
function shouldLogMqttDownstreamPayloadBytes(): boolean {
  const v = process.env.REACT_APP_DEBUG_MQTT_DOWNSTREAM_PAYLOAD?.trim().toLowerCase();
  return v === '1' || v === 'true' || v === 'yes';
}

let downstreamAudioVerboseLogCounter = 0;

/** 批量 raw 直连：整包为连续 PCM；总长非 4 的倍数时多为尾包非整帧或编码与 16bit 立体声不一致 */
function logAudioDownstreamRawConcat(deviceId: string, payloadLen: number): void {
  if (payloadLen % 4 !== 0) {
    console.warn('[WaveLink audio] 批量下行 raw 整包长度非 4 的倍数（16bit 立体声需整样本对齐；或为尾包）', {
      deviceId,
      byteLength: payloadLen,
    });
  }

  if (!shouldLogAudioDownstreamVerbose()) {
    return;
  }
  downstreamAudioVerboseLogCounter += 1;
  if (downstreamAudioVerboseLogCounter % 25 !== 0) {
    return;
  }
  console.log('[WaveLink audio downstream][batch raw concat]', {
    deviceId,
    mqttPayloadBytes: payloadLen,
  });
}

function logAudioDownstreamLegacy(deviceId: string, byteLength: number): void {
  if (byteLength % 4 !== 0) {
    console.warn('[WaveLink audio] 下行整包长度非 4 的倍数', { deviceId, byteLength });
  }
  if (!shouldLogAudioDownstreamVerbose()) {
    return;
  }
  downstreamAudioVerboseLogCounter += 1;
  if (downstreamAudioVerboseLogCounter % 25 !== 0) {
    return;
  }
  console.log('[WaveLink audio downstream][legacy 单帧]', { deviceId, byteLength });
}

function copyUint8ToArrayBuffer(frame: Uint8Array): ArrayBuffer {
  const ab = new ArrayBuffer(frame.byteLength);
  new Uint8Array(ab).set(frame);
  return ab;
}

function messageToUint8Array(message: string | Buffer | Uint8Array | ArrayBuffer): Uint8Array | null {
  if (typeof message === 'string') {
    return null;
  }
  if (message instanceof ArrayBuffer) {
    return new Uint8Array(message);
  }
  if (typeof Buffer !== 'undefined' && Buffer.isBuffer(message)) {
    return new Uint8Array(message.buffer, message.byteOffset, message.byteLength);
  }
  if (message instanceof Uint8Array) {
    return message;
  }
  if (message && typeof message === 'object' && 'buffer' in message) {
    const buf = (message as { buffer: ArrayBufferLike }).buffer;
    if (buf instanceof ArrayBuffer) {
      return new Uint8Array(buf);
    }
  }
  return null;
}

type WlDeviceStatusDebug = {
  log?: (deviceId: string, payload: unknown) => void;
  logParseError?: (deviceId: string, err: unknown) => void;
};

/** 默认关闭：status 主题推送很频，易刷屏。需调试时在 .env 设 REACT_APP_DEBUG_DEVICE_STATUS_MQTT=1 并重新 build / 重启 dev。 */
function shouldDebugScopedDeviceStatusMqtt(): boolean {
  const v = process.env.REACT_APP_DEBUG_DEVICE_STATUS_MQTT?.trim().toLowerCase();
  return v === '1' || v === 'true' || v === 'yes';
}

/** 开发环境默认开；生产需在 .env 设 REACT_APP_DEBUG_AUDIO_UPSTREAM=1 才打周期汇总（首次发布与对齐告警不受此开关影响） */
function shouldLogAudioUpstreamPeriodic(): boolean {
  if (process.env.NODE_ENV === 'development') {
    return true;
  }
  const v = process.env.REACT_APP_DEBUG_AUDIO_UPSTREAM?.trim().toLowerCase();
  return v === '1' || v === 'true' || v === 'yes';
}

/**
 * 生产包会剔除 src 内 console；需看原始 payload 时开 REACT_APP_DEBUG_DEVICE_STATUS_MQTT，
 * 由 public/wavelink-device-status-debug.js 里的 console 输出（index.html 已引入该脚本）。
 */
function wlScopedDeviceStatusDebugLog(deviceId: string, payload: unknown): void {
  if (!shouldDebugScopedDeviceStatusMqtt()) {
    return;
  }
  try {
    const g = globalThis as unknown as Record<string, WlDeviceStatusDebug | undefined>;
    g['__WL_DEVICE_STATUS_DEBUG__']?.log?.(deviceId, payload);
  } catch {
    /* 忽略 */
  }
}

function wlScopedDeviceStatusDebugParseError(deviceId: string, err: unknown): void {
  if (!shouldDebugScopedDeviceStatusMqtt()) {
    return;
  }
  try {
    const g = globalThis as unknown as Record<string, WlDeviceStatusDebug | undefined>;
    g['__WL_DEVICE_STATUS_DEBUG__']?.logParseError?.(deviceId, err);
  } catch {
    /* 忽略 */
  }
}

// 音频消息类型
export interface AudioMessage {
  userId: string;
  deviceId: string;
  audioData: ArrayBuffer;
  timestamp: number;
  sequence: number;
}

// 频谱消息类型
export interface SpectrumMessage {
  deviceId: string;
  spectrumData: Uint8Array | number[];
  timestamp: number;
  frequency: number;
  bandwidth: number;
}

// 设备状态消息类型
export interface DeviceStatusMessage {
  deviceId: string;
  status: 'online' | 'offline' | 'busy' | 'error';
  timestamp: number;
  metadata?: any;
}

// 音频事件回调
export interface AudioEventCallbacks {
  onAudioReceived?: (
    deviceId: string,
    audioData: ArrayBuffer,
    downstreamKind?: MqttAudioDownstreamKind
  ) => void;
  /** 512 点通常为 Uint8Array；非 512 字节二进制按 float32 解析时为 number[] */
  onSpectrumReceived?: (deviceId: string, spectrumData: Uint8Array | number[]) => void;
  onDeviceStatusUpdate?: (deviceId: string, status: any) => void;
  onAudioSent?: (deviceId: string, success: boolean) => void;
  onError?: (error: Error) => void;
}

/**
 * MQTT音频管理器
 */
export class MqttAudioManager {
  private mqttManager: MqttManager;
  private userId: string;
  private callbacks: AudioEventCallbacks = {};
  private subscribedDevices: Set<string> = new Set();
  private audioSequence: number = 0;
  /** 当前已订阅 device/{userId}/{deviceId}/status 的设备 id */
  private scopedStatusDeviceId: string | null = null;

  /** 音频上行审计：整页生命周期内首次成功 publish 打一条「凭据」级日志，便于与后端/设备责任划分 */
  private uplinkAuditFirstPublishDone = false;
  private uplinkAuditWindowStartMs = 0;
  private uplinkAuditPacketsInWindow = 0;
  private uplinkAuditBytesInWindow = 0;
  private uplinkAuditMinPayload = Number.POSITIVE_INFINITY;
  private uplinkAuditMaxPayload = 0;
  private uplinkAuditMisalignedInWindow = 0;
  private uplinkAuditLastDeviceId: string | null = null;

  constructor(mqttManager: MqttManager, userId: string) {
    this.mqttManager = mqttManager;
    this.userId = userId;
    
    // 设置MQTT事件回调
    this.setupMqttCallbacks();
  }

  /**
   * 设置MQTT事件回调
   */
  private setupMqttCallbacks(): void {
    const mqttCallbacks: MqttEventCallbacks = {
      onMessage: (topic: string, message: string | Buffer) => {
        this.handleMqttMessage(topic, message);
      },
      onError: (error: Error) => {
        console.error('MqttAudioManager: MQTT错误:', error);
        this.callbacks.onError?.(error);
      }
    };

    this.mqttManager.setCallbacks(mqttCallbacks);
  }

  /**
   * 处理MQTT消息
   */
  private handleMqttMessage(topic: string, message: string | Buffer): void {
    try {
      // 解析主题
      const topicParts = topic.split('/');
      
      if (topicParts.length < 3) {
        console.warn(`MqttAudioManager: 无效的主题格式: ${topic}`);
        return;
      }

      const messageType = topicParts[0];
      
      // 处理用户特定的设备状态主题: device/status/user/{userId}
      if (messageType === 'device' && topicParts[1] === 'status' && topicParts[2] === 'user') {
        this.handleUserDeviceStatusMessage(topic, message);
        return;
      }

      // device/{userId}/{deviceId}/status（主题中的 userId 与构造器传入的需一致，统一成字符串比较）
      if (
        messageType === 'device' &&
        topicParts.length === 4 &&
        topicParts[3] === 'status' &&
        String(topicParts[1]) === String(this.userId)
      ) {
        this.handleScopedDeviceStatusMessage(message, topicParts[2]);
        return;
      }
      
      // 处理其他主题格式
      const deviceId = topicParts[2];

      switch (messageType) {
        case 'audio':
          this.handleAudioMessage(topic, message, deviceId);
          break;
        case 'spectrum':
          this.handleSpectrumMessage(topic, message, deviceId);
          break;
        case 'device':
          this.handleDeviceStatusMessage(topic, message, deviceId);
          break;
        default:
          // 未知消息类型，静默忽略
          break;
      }
    } catch (error) {
      console.error('MqttAudioManager: 处理MQTT消息失败:', error);
      this.callbacks.onError?.(error as Error);
    }
  }

  /**
   * 处理音频消息。
   * Codec2（…/downstream/c2）时 payload 长度由设备决定（如单帧 8B、多帧 88B），整包原样交给上层；拆帧与解码在 AudioManager。
   */
  private handleAudioMessage(topic: string, message: string | Buffer | Uint8Array | ArrayBuffer, deviceId: string): void {
    try {
      if (!topic.includes('/downstream')) {
        return;
      }

      const bytes = messageToUint8Array(message);
      if (!bytes || bytes.length === 0) {
        if (typeof message === 'string') {
          console.warn('MqttAudioManager: 音频下行消息不是二进制数据');
        }
        return;
      }

      if (shouldLogMqttDownstreamPayloadBytes()) {
        console.warn('[WaveLink MQTT downstream payload]', {
          topic,
          deviceId,
          byteLength: bytes.length,
          payloadBytes: Array.from(bytes),
        });
      }

      const onAudio = this.callbacks.onAudioReceived;
      if (!onAudio) {
        return;
      }

      const downstreamKind = getMqttAudioDownstreamKindFromTopic(topic);
      const audioData = copyUint8ToArrayBuffer(bytes);

      if (downstreamKind === 'pcm' && isMqttAudioDownstreamBatchEnabled()) {
        logAudioDownstreamRawConcat(deviceId, bytes.length);
        Promise.resolve().then(() => {
          onAudio(deviceId, audioData, 'pcm');
        });
        return;
      }

      if (downstreamKind === 'pcm') {
        logAudioDownstreamLegacy(deviceId, bytes.length);
      }

      Promise.resolve().then(() => {
        onAudio(deviceId, audioData, downstreamKind);
      });
    } catch (error) {
      console.error('MqttAudioManager: 处理音频消息失败:', error);
      this.callbacks.onError?.(error as Error);
    }
  }

  /**
   * 处理频谱消息
   */
  private handleSpectrumMessage(topic: string, message: string | Buffer | Uint8Array, deviceId: string): void {
    try {
    
      if (message instanceof Buffer) {
        const spectrumData = this.parseSpectrumData(message);
        this.callbacks.onSpectrumReceived?.(deviceId, spectrumData);
      } else if (message instanceof Uint8Array) {
        this.callbacks.onSpectrumReceived?.(deviceId, new Uint8Array(message));
      } else {
        console.warn('MqttAudioManager: 频谱消息不是二进制数据');
      }
    } catch (error) {
      console.error('MqttAudioManager: 处理频谱消息失败:', error);
      this.callbacks.onError?.(error as Error);
    }
  }

  /**
   * 处理设备状态消息
   */
  private handleDeviceStatusMessage(topic: string, message: string | Buffer | Uint8Array, deviceId: string): void {
    try {
      if (topic.includes('/status')) {
        const messageStr = message.toString();
        const statusMessage = JSON.parse(messageStr) as DeviceStatusMessage;
        this.callbacks.onDeviceStatusUpdate?.(deviceId, statusMessage.status);
      }
    } catch (error) {
      console.error('MqttAudioManager: 处理设备状态消息失败:', error);
      this.callbacks.onError?.(error as Error);
    }
  }

  /**
   * 处理 device/{userId}/{deviceId}/status 上的状态推送
   */
  private handleScopedDeviceStatusMessage(message: string | Buffer | Uint8Array, topicDeviceId: string): void {
    try {
      const messageStr = message.toString();
      const raw = JSON.parse(messageStr);
      wlScopedDeviceStatusDebugLog(topicDeviceId, raw);
      const inner = raw.data ?? raw;
      const hasVfo =
        inner.vfoAFrequency !== undefined ||
        inner.vfoBFrequency !== undefined ||
        (raw.data && (raw.data.vfoAFrequency !== undefined || raw.data.vfoBFrequency !== undefined));

      if (hasVfo) {
        const d = raw.data ?? inner;
        const fullStatus = {
          deviceId: raw.deviceId ?? topicDeviceId,
          vfoAFrequency: d.vfoAFrequency ?? 0,
          vfoBFrequency: d.vfoBFrequency ?? 0,
          vfoAMode: d.vfoAMode ?? 'Unknown',
          vfoBMode: d.vfoBMode ?? 'Unknown',
          isOnline: d.isOnline !== undefined ? d.isOnline : true,
          deviceIp: d.deviceIp ?? '',
          timestamp: d.timestamp ?? Date.now(),
        };
        this.callbacks.onDeviceStatusUpdate?.(fullStatus.deviceId, fullStatus);
        return;
      }

      if (typeof raw.status === 'string') {
        this.callbacks.onDeviceStatusUpdate?.(topicDeviceId, raw.status);
        return;
      }

      this.callbacks.onDeviceStatusUpdate?.(topicDeviceId, raw);
    } catch (error) {
      wlScopedDeviceStatusDebugParseError(topicDeviceId, error);
      console.error('MqttAudioManager: 处理 device/{userId}/{deviceId}/status 消息失败:', error);
      this.callbacks.onError?.(error as Error);
    }
  }

  /**
   * 处理用户特定的设备状态消息
   */
  private handleUserDeviceStatusMessage(topic: string, message: string | Buffer): void {
    try {
      const messageStr = message.toString();
      const statusData = JSON.parse(messageStr);
      
      // 提取设备ID和状态信息
      const deviceId = statusData.deviceId;
      const deviceStatus = statusData.data;
      
      if (deviceId && deviceStatus) {
        // 构建包含频率和模式信息的完整状态对象
        const fullStatus = {
          deviceId: deviceId,
          vfoAFrequency: deviceStatus.vfoAFrequency || 0,
          vfoBFrequency: deviceStatus.vfoBFrequency || 0,
          vfoAMode: deviceStatus.vfoAMode || 'Unknown',
          vfoBMode: deviceStatus.vfoBMode || 'Unknown',
          isOnline: deviceStatus.isOnline || false,
          deviceIp: deviceStatus.deviceIp || '',
          timestamp: deviceStatus.timestamp || Date.now()
        };
        
        // 触发设备状态更新回调，传递完整的状态信息
        this.callbacks.onDeviceStatusUpdate?.(deviceId, fullStatus);
      } else {
        console.warn('MqttAudioManager: 用户设备状态消息格式无效:', statusData);
      }
    } catch (error) {
      console.error('MqttAudioManager: 处理用户设备状态消息失败:', error);
      this.callbacks.onError?.(error as Error);
    }
  }

  /**
   * 解析频谱数据
   */
  private parseSpectrumData(buffer: Buffer): Uint8Array | number[] {
    if (buffer.length === 512) {
      return new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);
    }
    const float32Array = new Float32Array(buffer.buffer, buffer.byteOffset, buffer.byteLength / 4);
    return Array.from(float32Array);
  }

  /**
   * 订阅设备音频
   */
  async subscribeToDeviceAudio(deviceId: string): Promise<void> {
    try {
      const pcmTopic = getMqttAudioDownstreamPcmTopic(deviceId);
      const codec2Topic = getMqttAudioDownstreamCodec2Topic(deviceId);
      const spectrumTopic = `spectrum/device/${deviceId}`;
      // 修复：后端只发布 device/status/user/{userId}，不发布 device/{deviceId}/status
      const userStatusTopic = `device/status/user/${this.userId}`;

      await Promise.all([
        this.mqttManager.subscribe(pcmTopic, 0),
        this.mqttManager.subscribe(codec2Topic, 0),
      ]);
      
      // 订阅频谱主题 (QoS 1 - 至少一次)
      await this.mqttManager.subscribe(spectrumTopic, 1);
      
      // 订阅用户特定的设备状态主题 (QoS 1 - 至少一次)
      // 注意：后端只发布用户特定的状态主题，不发布设备级别的状态主题
      await this.mqttManager.subscribe(userStatusTopic, 1);

      this.subscribedDevices.add(deviceId);
    } catch (error) {
      console.error(`MqttAudioManager: 订阅设备音频失败 - 设备ID: ${deviceId}:`, error);
      throw error;
    }
  }

  /**
   * 验证设备音频订阅是否成功
   */
  async verifySubscription(deviceId: string): Promise<boolean> {
    try {
      const pcmTopic = getMqttAudioDownstreamPcmTopic(deviceId);
      const codec2Topic = getMqttAudioDownstreamCodec2Topic(deviceId);
      const spectrumTopic = `spectrum/device/${deviceId}`;
      const userStatusTopic = `device/status/user/${this.userId}`;

      const audioSubscribed =
        this.mqttManager.isSubscribed(pcmTopic) && this.mqttManager.isSubscribed(codec2Topic);
      const spectrumSubscribed = this.mqttManager.isSubscribed(spectrumTopic);
      const statusSubscribed = this.mqttManager.isSubscribed(userStatusTopic);

      const allSubscribed = audioSubscribed && spectrumSubscribed && statusSubscribed;
      return allSubscribed;
    } catch (error) {
      console.error(`MqttAudioManager: 验证订阅状态失败 - 设备ID: ${deviceId}:`, error);
      return false;
    }
  }

  /**
   * 取消订阅设备音频
   */
  async unsubscribeFromDeviceAudio(deviceId: string): Promise<void> {
    try {
      const pcmTopic = getMqttAudioDownstreamPcmTopic(deviceId);
      const codec2Topic = getMqttAudioDownstreamCodec2Topic(deviceId);
      const spectrumTopic = `spectrum/device/${deviceId}`;
      // 修复：不再取消订阅 device/{deviceId}/status，因为已经移除此订阅
      // 注意：userStatusTopic 是用户级别的，不应该在取消设备订阅时取消

      // 取消订阅设备相关的主题（不包括用户状态主题）
      await Promise.all([
        this.mqttManager.unsubscribe(pcmTopic),
        this.mqttManager.unsubscribe(codec2Topic),
        this.mqttManager.unsubscribe(spectrumTopic),
      ]);

      this.subscribedDevices.delete(deviceId);
      // 注意：用户状态主题 (device/status/user/{userId}) 在用户断开连接时统一取消订阅
    } catch (error) {
      console.error(`MqttAudioManager: 取消订阅设备音频失败 - 设备ID: ${deviceId}:`, error);
      throw error;
    }
  }

  /**
   * MQTT 边界审计日志：证明前端发出的上行 payload 形态（原始二进制、长度与立体声帧对齐等）。
   */
  private auditUplinkMqttPublish(deviceId: string, topic: string, byteLength: number): void {
    const isCodec2Uplink = topic.includes('/upstream/c2');
    const aligned4 = byteLength % 4 === 0;
    const stereoFrames = byteLength / 4;
    const alignedCodec2Frame = !isCodec2Uplink || byteLength % 8 === 0;

    if (!isCodec2Uplink && !aligned4) {
      console.warn(
        '[WaveLink 音频上行] 载荷长度非 4 的倍数（与 16bit 立体声 PCM 单帧=4 字节不一致；请查 Worklet/重采样）',
        { deviceId, topic, payloadBytes: byteLength }
      );
    }
    if (isCodec2Uplink && byteLength % 8 !== 0) {
      console.warn('[WaveLink 音频上行] Codec2 上行载荷长度非 8 的倍数（与常见 mode 3200 单帧 8B 不一致）', {
        deviceId,
        topic,
        payloadBytes: byteLength,
      });
    }

    if (!this.uplinkAuditFirstPublishDone) {
      this.uplinkAuditFirstPublishDone = true;
      console.info('[WaveLink 音频上行] 首次 MQTT 发布成功（前端侧「已按约定发二进制」可追溯凭据）', {
        topic,
        deviceId,
        payloadBytes: byteLength,
        stereoFramesInPayload: isCodec2Uplink ? undefined : stereoFrames,
        payloadAlignedTo4Bytes: isCodec2Uplink ? undefined : aligned4,
        mqttMessage: '原始二进制 Buffer，非 JSON、非 Base64 文本',
        pcmContract: isCodec2Uplink
          ? 'Codec2 比特流（与 REACT_APP_CODEC2_MODE / 设备约定一致；后端可透传 UDP）'
          : '48kHz | 16bit 有符号 小端 | 立体声样本交错 L,R,L,R,…（由 AudioWorklet 与重采样链路保证）',
        qos: 0,
        sequence: this.audioSequence,
      });
    }

    if (!shouldLogAudioUpstreamPeriodic()) {
      return;
    }

    const now = Date.now();
    if (this.uplinkAuditWindowStartMs === 0) {
      this.uplinkAuditWindowStartMs = now;
    }
    this.uplinkAuditLastDeviceId = deviceId;
    this.uplinkAuditPacketsInWindow += 1;
    this.uplinkAuditBytesInWindow += byteLength;
    this.uplinkAuditMinPayload = Math.min(this.uplinkAuditMinPayload, byteLength);
    this.uplinkAuditMaxPayload = Math.max(this.uplinkAuditMaxPayload, byteLength);
    if ((!isCodec2Uplink && !aligned4) || (isCodec2Uplink && !alignedCodec2Frame)) {
      this.uplinkAuditMisalignedInWindow += 1;
    }

    const elapsed = now - this.uplinkAuditWindowStartMs;
    if (elapsed < 4000 || this.uplinkAuditPacketsInWindow === 0) {
      return;
    }

    console.info('[WaveLink 音频上行] 周期汇总（约 4s，可配合首次凭据日志做联调）', {
      deviceId: this.uplinkAuditLastDeviceId,
      packetsInWindow: this.uplinkAuditPacketsInWindow,
      bytesInWindow: this.uplinkAuditBytesInWindow,
      payloadBytesMin: this.uplinkAuditMinPayload,
      payloadBytesMax: this.uplinkAuditMaxPayload,
      misalignedPacketsInWindow: this.uplinkAuditMisalignedInWindow,
      topicPattern: 'audio/user/{deviceId}/upstream | audio/user/{deviceId}/upstream/c2',
    });

    this.uplinkAuditWindowStartMs = now;
    this.uplinkAuditPacketsInWindow = 0;
    this.uplinkAuditBytesInWindow = 0;
    this.uplinkAuditMinPayload = Number.POSITIVE_INFINITY;
    this.uplinkAuditMaxPayload = 0;
    this.uplinkAuditMisalignedInWindow = 0;
  }

  /**
   * 发送音频数据到设备
   * @param uplinkRoute PCM 走 `audio/user/{id}/upstream`；Codec2 走 `audio/user/{id}/upstream/c2`
   */
  async sendAudioToDevice(
    deviceId: string,
    audioData: ArrayBuffer,
    uplinkRoute: MqttVoiceUplinkRoute = 'user_pcm'
  ): Promise<void> {
    try {
      const topic =
        uplinkRoute === 'user_codec2'
          ? getMqttAudioUpstreamCodec2UserTopic(deviceId)
          : getMqttAudioUpstreamUserTopic(deviceId);
      const message: AudioMessage = {
        userId: this.userId,
        deviceId: deviceId,
        audioData: audioData,
        timestamp: Date.now(),
        sequence: ++this.audioSequence
      };

      // 将音频数据转换为Buffer
      const buffer = Buffer.from(audioData);

      // 发布音频数据 (QoS 0 - 最快传输)
      await this.mqttManager.publish(topic, buffer, 0);

      this.auditUplinkMqttPublish(deviceId, topic, buffer.byteLength);

      this.callbacks.onAudioSent?.(deviceId, true);
    } catch (error) {
      console.error(`MqttAudioManager: 发送音频数据失败 - 设备ID: ${deviceId}:`, error);
      this.callbacks.onAudioSent?.(deviceId, false);
      throw error;
    }
  }

  /**
   * 发送PTT控制命令
   */
  async sendPttCommand(deviceId: string, action: 'start' | 'stop'): Promise<void> {
    try {
      const topic = `ptt/device/${deviceId}/control`;
      const message = {
        action: action,
        userId: this.userId,
        deviceId: deviceId,
        timestamp: Date.now()
      };

      // 发布PTT命令 (QoS 2 - 恰好一次)
      await this.mqttManager.publishJson(topic, message, 2);
    } catch (error) {
      console.error(`MqttAudioManager: 发送PTT命令失败 - 设备ID: ${deviceId}:`, error);
      throw error;
    }
  }

  /**
   * 发送频率设置命令
   */
  async sendFrequencyCommand(deviceId: string, vfoAFrequency: string, vfoBFrequency: string): Promise<void> {
    try {
      const topic = `device/${this.userId}/${deviceId}/frequency`;
      const message = {
        vfoAFrequency: vfoAFrequency,
        vfoBFrequency: vfoBFrequency,
        userId: this.userId,
        deviceId: deviceId,
        timestamp: Date.now()
      };

      // 发布频率命令 (QoS 2 - 恰好一次)
      await this.mqttManager.publishJson(topic, message, 2);
    } catch (error) {
      console.error(`MqttAudioManager: 发送频率命令失败 - 设备ID: ${deviceId}:`, error);
      throw error;
    }
  }

  /**
   * 发送模式设置命令
   */
  async sendModeCommand(deviceId: string, vfoAMode: string, vfoBMode: string): Promise<void> {
    try {
      const topic = `device/${this.userId}/${deviceId}/mode`;
      const message = {
        vfoAMode: vfoAMode,
        vfoBMode: vfoBMode,
        userId: this.userId,
        deviceId: deviceId,
        timestamp: Date.now()
      };

      // 发布模式命令 (QoS 2 - 恰好一次)
      await this.mqttManager.publishJson(topic, message, 2);
    } catch (error) {
      console.error(`MqttAudioManager: 发送模式命令失败 - 设备ID: ${deviceId}:`, error);
      throw error;
    }
  }

  /**
   * 设置事件回调
   */
  setCallbacks(callbacks: AudioEventCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * 获取已订阅的设备列表
   */
  getSubscribedDevices(): string[] {
    return Array.from(this.subscribedDevices);
  }

  /**
   * 检查是否已订阅设备
   */
  isSubscribedToDevice(deviceId: string): boolean {
    return this.subscribedDevices.has(deviceId);
  }

  /**
   * 订阅 device/{userId}/{deviceId}/status（不再向 …/command 发布 status_request）
   */
  async subscribeToScopedDeviceStatus(deviceId: string): Promise<void> {
    const topic = `device/${this.userId}/${deviceId}/status`;
    try {
      if (
        this.scopedStatusDeviceId !== null &&
        String(this.scopedStatusDeviceId) === String(deviceId) &&
        this.mqttManager.isSubscribed(topic)
      ) {
        return;
      }
      if (this.scopedStatusDeviceId !== null && String(this.scopedStatusDeviceId) !== String(deviceId)) {
        await this.unsubscribeFromScopedDeviceStatus(this.scopedStatusDeviceId);
      }
      await this.mqttManager.subscribe(topic, 1);
      this.scopedStatusDeviceId = String(deviceId);
      console.log(`MqttAudioManager: 已订阅设备状态主题 ${topic}`);
    } catch (error) {
      console.error(`MqttAudioManager: 订阅设备状态主题失败 - 设备ID: ${deviceId}:`, error);
      throw error;
    }
  }

  async unsubscribeFromScopedDeviceStatus(deviceId: string): Promise<void> {
    const topic = `device/${this.userId}/${deviceId}/status`;
    try {
      await this.mqttManager.unsubscribe(topic);
      if (this.scopedStatusDeviceId !== null && String(this.scopedStatusDeviceId) === String(deviceId)) {
        this.scopedStatusDeviceId = null;
      }
      console.log(`MqttAudioManager: 已取消订阅设备状态主题 ${topic}`);
    } catch (error) {
      console.error(`MqttAudioManager: 取消订阅设备状态主题失败 - 设备ID: ${deviceId}:`, error);
      throw error;
    }
  }

  /**
   * 获取用户ID
   */
  getUserId(): string {
    return this.userId;
  }

  /**
   * 销毁音频管理器
   */
  async dispose(): Promise<void> {
    // 取消订阅所有设备
    const unsubscribePromises = Array.from(this.subscribedDevices).map(deviceId => 
      this.unsubscribeFromDeviceAudio(deviceId)
    );
    
    await Promise.all(unsubscribePromises);

    if (this.scopedStatusDeviceId) {
      try {
        await this.unsubscribeFromScopedDeviceStatus(this.scopedStatusDeviceId);
      } catch (error) {
        console.error(`MqttAudioManager: dispose 时取消 scoped status 订阅失败:`, error);
      }
    }
    
    // 取消订阅用户状态主题
    try {
      const userStatusTopic = `device/status/user/${this.userId}`;
      await this.mqttManager.unsubscribe(userStatusTopic);
    } catch (error) {
      console.error(`MqttAudioManager: 取消订阅用户状态主题失败 - 用户ID: ${this.userId}:`, error);
    }
    
    this.subscribedDevices.clear();
  }
}

// 创建单例实例
let mqttAudioManagerInstance: MqttAudioManager | null = null;

/**
 * 获取MQTT音频管理器实例
 */
export function getMqttAudioManager(): MqttAudioManager | null {
  return mqttAudioManagerInstance;
}

/**
 * 创建MQTT音频管理器实例
 */
export function createMqttAudioManager(mqttManager: MqttManager, userId: string): MqttAudioManager {
  if (mqttAudioManagerInstance !== null) {
    throw new Error(
      'MqttAudioManager: 单例已存在，请先 await destroyMqttAudioManager()（登出/销毁 MQTT 时会调用）'
    );
  }
  mqttAudioManagerInstance = new MqttAudioManager(mqttManager, String(userId));
  return mqttAudioManagerInstance;
}

/**
 * 销毁MQTT音频管理器单例（必须先清空引用再 dispose，避免换账号后 create 仍返回旧 userId 实例）
 */
export async function destroyMqttAudioManager(): Promise<void> {
  if (!mqttAudioManagerInstance) {
    return;
  }
  const inst = mqttAudioManagerInstance;
  mqttAudioManagerInstance = null;
  try {
    await inst.dispose();
  } catch (e) {
    console.error('MqttAudioManager: destroyMqttAudioManager dispose 失败', e);
  }
}
